import React, { useState } from 'react';
import { API_URL } from '../config';
import { pdf } from '@react-pdf/renderer';
import { toast } from 'react-toastify';
import { FaSpinner, FaFilePdf } from 'react-icons/fa';
import ClientInvoicePDF from './ClientInvoicePDF';
import { getCompanyDetails } from '../config/CompanyDetails';

// ============================================
// CLIENT INVOICE PDF BUTTON
// Fetches the full invoice detail (items + job + quote + payments), then
// generates the TAX INVOICE PDF using the same layout as the proforma.
// Company/bank details always come from the single source (settings).
// ============================================
const ClientInvoicePDFButton = ({ invoiceId, invoice, onSuccess, className, iconOnly }) => {
  const [isGenerating, setIsGenerating] = useState(false);

  const handleGeneratePDF = async () => {
    setIsGenerating(true);
    try {
      let detail = { invoice, items: invoice?.items || [], job: invoice?.job || null, quote: invoice?.quote || null, payments: invoice?.payments || [] };

      // Fetch full detail if we only have a list row (no items)
      if (!invoice?.items) {
        const res = await fetch(`${API_URL}/invoices/${invoiceId || invoice?.id}`);
        const data = await res.json();
        if (data.success) {
          detail = {
            invoice: data.invoice,
            items: data.items || [],
            job: data.invoice?.job || data.invoice?.jobs || null,
            quote: data.invoice?.quote || data.invoice?.quotes || null,
            payments: data.payments || []
          };
        }
      }

      const company = await getCompanyDetails();

      const blob = await pdf(
        <ClientInvoicePDF
          invoice={detail.invoice}
          job={detail.job}
          quote={detail.quote}
          payments={detail.payments}
          company={company}
        />
      ).toBlob();

      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Invoice_${detail.invoice?.invoice_number || invoiceId || 'INV'}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(url), 1000);

      toast.success(`📄 Invoice ${detail.invoice?.invoice_number || ''} downloaded`);
      if (onSuccess) onSuccess();
    } catch (error) {
      console.error('Error generating invoice PDF:', error);
      toast.error('Failed to generate invoice PDF. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <button
      onClick={handleGeneratePDF}
      disabled={isGenerating}
      title="Download PDF"
      className={iconOnly
        ? "text-gray-400 hover:text-red-600 transition-colors p-1 bg-transparent shadow-none"
        : (className || "flex items-center gap-2 px-3 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-xs font-medium disabled:opacity-50 transition-colors")}
    >
      {isGenerating ? <FaSpinner className="animate-spin" size={12} /> : <FaFilePdf size={16} />}
      {!iconOnly && (isGenerating ? 'Generating...' : 'Download PDF')}
    </button>
  );
};

export default ClientInvoicePDFButton;
